// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '../../../../../store/session';
import actionsSession from '@/app/modules/session/api/actionsSession';
// Components
import ActivateDeactivate from './actions/ActivateDeactivate';
import ModalResetPassword from '@/app/modules/users/monetic/components/modals/ModalResetPassword';
import ModalRemoveUser from '../modals/ModalRemoveUser';
import ModalPermissions from '../modals/permissions/ModalPermissions';
import Dropdown from '../../../../../../common-utils/components/renders/dropdown/Dropdown';
// Common
import ICONS from '../../../../../../common-utils/enums/icons';
import Permissions from '../../../../../permissions/Permissions';
import userFields from '../../fields/userFields';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import TAB_KEYS_HISTORICS from '@/app/root/enums/tabs/HistoricsTabs';
import RouteUrls from '@/app/root/enums/RouteUrls';
import historicsFields from '@/app/modules/historics/fields/historicsFields';
// Lang
import { nsUser } from '@/app/i18n/namespaces';
import { useNavigate } from 'react-router-dom';
import { buildUrl } from '@/common-utils/functions/restUtils';

/**
 *
 * @param root0
 * @param root0.user
 * @param root0.setLaunchRefetch
 * @returns {JSX} DropdownUserActionsRow
 */
function DropdownUserActionsRow({ user, setLaunchRefetch }) {

    // Router
    const navigate = useNavigate();

    // Store
    const session = useSessionStore(s => s.session);

    const selectedUserId = user ? user[userFields.UUID.key] : null;
    const loggedUserId = session.uuid;
    const permissions = session.permissions;

    // Api calls
    const submitConnectAs = actionsSession.connectAs(() => { }, () => { });

    // Functions & states
    const [showModalResetPassword, setShowModalResetPassword] = useState(false);
    const [showModalRemoveUser, setShowModalRemoveUser] = useState(false);
    const [showModalUpdatePermissions, setShowModalUpdatePermissions] = useState(false);

    return (
        <>
            <Dropdown
                dataCy='user-actions-dropdown-row'
                triggerValues={{ variant: 'ghostNoBorder', icon: ICONS.MORE }}
                actions={[
                    ...ActivateDeactivate(user, permissions, setLaunchRefetch),
                    {
                        icon: ICONS.CONTACT_MAIL,
                        entity: 'actions.row.reset_password',
                        namespace: nsUser,
                        onClick: () => setShowModalResetPassword(true),
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [
                                Permissions.USER_RESEND_ACTIVATION_MAIL.name
                            ]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.USER_SETTINGS,
                        entity: 'actions.row.permissions_update',
                        namespace: nsUser,
                        onClick: () => setShowModalUpdatePermissions(true),
                        visible: true,
                        disabled: ((selectedUserId === loggedUserId) || !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [
                                Permissions.USER_CREATE.name
                            ]
                        })),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.SUBSCRIPTION_TERMINATE,
                        entity: 'actions.row.remove',
                        namespace: nsUser,
                        className: 'text-danger',
                        onClick: () => setShowModalRemoveUser(true),
                        visible: true,
                        disabled: ((selectedUserId === loggedUserId) || !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [
                                Permissions.USER_REMOVE.name
                            ]
                        })),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.USER_CONNECT_AS,
                        entity: 'actions.row.connect_as',
                        namespace: nsUser,
                        onClick: () => submitConnectAs.mutate(selectedUserId),
                        visible: true,
                        disabled: ((selectedUserId === loggedUserId) || !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.USER_LOGIN_AS_USER.name]
                        })),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.HISTORY,
                        entity: 'actions.row.historics_access',
                        namespace: nsUser,
                        onClick: () => {
                            const userEmail = user ? user[userFields.EMAIL.key] : null;
                            const userHistorics = buildUrl(RouteUrls.HISTORICS.url,
                                {
                                    view: TAB_KEYS_HISTORICS.USERS.name,
                                    filters: userEmail ? [
                                        {
                                            key: historicsFields.USERS_HISTORICS.EMAIL.key,
                                            value: userEmail
                                        }
                                    ] : []
                                }
                            );
                            navigate(userHistorics);
                        },
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.LIST_HISTORY.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    }
                ]} />
            {
                showModalResetPassword &&
                <ModalResetPassword
                    isOpen={showModalResetPassword}
                    setIsOpen={setShowModalResetPassword}
                    userId={selectedUserId}
                    userEmail={user[userFields.EMAIL.key]}
                />
            }
            {
                showModalRemoveUser &&
                <ModalRemoveUser
                    isOpen={showModalRemoveUser}
                    setIsOpen={setShowModalRemoveUser}
                    userId={selectedUserId}
                    userEmail={user[userFields.EMAIL.key]}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalUpdatePermissions &&
                <ModalPermissions
                    isOpen={showModalUpdatePermissions}
                    setIsOpen={setShowModalUpdatePermissions}
                    userId={selectedUserId}
                    organizationId={user[userFields.ORGANIZATION_UUID.key]}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
        </>
    );
}
DropdownUserActionsRow.propTypes = {
    user: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};
export default DropdownUserActionsRow;